import React, { useCallback, useEffect, useState } from 'react';
import SiteNav, { SHOP_NAV_LINKS } from '../components/SiteNav';
import useNavSolid from '../hooks/useNavSolid';
import { createCheckout, fetchShopConfig, fetchShopProducts } from '../api/shop';
import { PLUSHIE_PRODUCT } from '../data/shopProduct';
import { PUBLIC_SITE_ORIGIN, STRIPE_TEST_MODE } from '../config';

function formatMoney(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    cents / 100
  );
}

function CheckoutBanner() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('checkout');
  if (status === 'success') {
    return (
      <p className="shop-banner shop-banner--success" role="status">
        Thank you! Your order is confirmed. We&apos;ll be in touch if we need anything.
      </p>
    );
  }
  if (status === 'canceled') {
    return (
      <p className="shop-banner shop-banner--muted" role="status">
        Checkout was canceled. You can try again when you&apos;re ready.
      </p>
    );
  }
  return null;
}

export default function ShopPage() {
  const navSolid = useNavSolid(24);
  const productMeta = PLUSHIE_PRODUCT;
  const [apiProduct, setApiProduct] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [email, setEmail] = useState('');

  const loadShop = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [products, config] = await Promise.all([fetchShopProducts(), fetchShopConfig()]);
      const match = products.find(p => p.slug === productMeta.slug);
      setApiProduct(match || null);
      setStripeEnabled(Boolean(config.stripeEnabled));
      if (!match) setError('This product is not available right now.');
      else if (!config.stripeEnabled) setError('Checkout is temporarily unavailable. Please try again later.');
    } catch (e) {
      setError(e.message || 'Failed to load shop.');
    } finally {
      setLoading(false);
    }
  }, [productMeta.slug]);

  useEffect(() => {
    loadShop();
    document.title = `${productMeta.name} — Libby the Lion & Friends`;
    const canonical = `${PUBLIC_SITE_ORIGIN}/shop`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonical);
  }, [loadShop, productMeta.name]);

  const inStock = apiProduct?.inStock ?? false;
  const showInventory = apiProduct?.trackInventory && apiProduct.inventoryCount != null;
  const priceCents = apiProduct?.priceCents;

  const handleCheckout = async () => {
    if (!apiProduct || !inStock) return;
    setCheckoutLoading(true);
    setError('');
    try {
      const { checkoutUrl } = await createCheckout({
        items: [{ productSlug: apiProduct.slug, quantity: 1 }],
        customerEmail: email.trim() || undefined,
      });
      window.location.href = checkoutUrl;
    } catch (e) {
      setError(e.message || 'Checkout failed.');
      setCheckoutLoading(false);
      await loadShop();
    }
  };

  return (
    <div className="v2-shell shop-shell">
      <SiteNav solid={navSolid} links={SHOP_NAV_LINKS} brandHref="/" />
      <header className="shop-hero">
        <div className="shop-hero__inner">
          <p className="section-eyebrow">Official shop</p>
          <h1 className="section-display">Libby&apos;s Pop-Up Shop</h1>
          <p className="shop-hero__lead">Bring Libby home — soft, sparkly, and ready to roar into your heart.</p>
        </div>
      </header>

      <main className="shop-main">
        <CheckoutBanner />
        {STRIPE_TEST_MODE && (
          <p className="shop-banner shop-banner--muted" role="status">
            Test checkout — payments use Stripe test mode. No real charges.
          </p>
        )}
        {error && (
          <p className="shop-banner shop-banner--error" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <p className="shop-muted">Loading product…</p>
        ) : (
          <article className="shop-feature" aria-labelledby="shop-product-heading">
            <div className="shop-feature__media">
              <img
                src={productMeta.image}
                alt={productMeta.imageAlt}
                className="shop-feature__image"
                width={640}
                height={640}
                decoding="async"
              />
            </div>
            <div className="shop-feature__body">
              <p className="section-eyebrow shop-feature__eyebrow">
                {inStock ? 'Available now' : 'Sold out'}
              </p>
              <h2 id="shop-product-heading" className="shop-feature__title">
                {apiProduct?.name || productMeta.name}
              </h2>
              <p className="shop-feature__tagline">{productMeta.tagline}</p>
              <p className="shop-feature__description">{productMeta.description}</p>
              {priceCents != null && (
                <p className="shop-feature__price">{formatMoney(priceCents, apiProduct?.currency)}</p>
              )}
              {showInventory && (
                <p className={`shop-feature__stock${inStock ? '' : ' shop-feature__stock--out'}`} role="status">
                  {inStock ? `${apiProduct.inventoryCount} in stock` : 'Sold out'}
                </p>
              )}
              {inStock && stripeEnabled ? (
                <>
                  <label className="shop-email-label">
                    Email (optional)
                    <input
                      type="email"
                      className="shop-email-input"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn-buy-amazon btn-buy-amazon--neutral shop-feature__buy"
                    disabled={checkoutLoading}
                    onClick={handleCheckout}
                  >
                    {checkoutLoading ? 'Redirecting…' : productMeta.buyLabel}
                  </button>
                </>
              ) : (
                <button type="button" className="btn-buy-amazon btn-buy-amazon--neutral shop-feature__buy" disabled>
                  {inStock ? 'Checkout unavailable' : 'Sold out'}
                </button>
              )}
              <p className="shop-footnote">Secure checkout powered by Stripe. Inventory updates after each order.</p>
            </div>
          </article>
        )}
      </main>

      <footer className="v2-footer shop-footer">
        <div className="footer-title-band">
          <p className="footer-line">
            <a href="/">← Back to Libby the Lion &amp; Friends</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
